import * as THREE from 'three'

/**
 * 规范化 `#RGB` / `#RRGGBB` / 带 alpha 的 `#RRGGBBAA` 为 7 位 `#RRGGBB`（供 canvas / Three 使用）。
 */
export function normalizeHex7(input: string): string {
  const h = input.trim()
  if (h.startsWith('#') && h.length === 4) {
    const r = h[1]
    const g = h[2]
    const b = h[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  if (h.startsWith('#') && (h.length === 7 || h.length === 9)) return h.slice(0, 7).toLowerCase()
  return '#ffffff'
}

export interface GradientStopLike {
  hex: string
  /** 0–100 */
  position: number
}

/** CSS 线性渐变：左上 → 右下 与 `135deg` 约定一致（与 Canvas 对角线对齐）。 */
export const DEFAULT_GRADIENT_ANGLE_DEG = 135

/**
 * 生成用于 CSS `background` 的线性渐变字符串（默认左上到右下）。
 */
export function toCssLinearGradient(
  stops: readonly GradientStopLike[],
  angleDeg: number = DEFAULT_GRADIENT_ANGLE_DEG,
): string {
  const sorted = [...stops].sort((a, b) => a.position - b.position || a.hex.localeCompare(b.hex))
  const parts = sorted.map(
    (s) => `${normalizeHex7(s.hex)} ${(Math.round(s.position * 10) / 10).toString().replace(/\.0$/, '')}%`,
  )
  return `linear-gradient(${angleDeg}deg, ${parts.join(', ')})`
}

/**
 * 根据色标生成对角线（左上→右下）`CanvasTexture`，用作 Three.js `scene.background`。
 */
export function createBackgroundGradientCanvasTexture(
  stops: readonly GradientStopLike[],
): THREE.CanvasTexture {
  const S = 512
  const canvas = document.createElement('canvas')
  canvas.width = S
  canvas.height = S
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('2D context unavailable')
  }
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  const grd = ctx.createLinearGradient(0, 0, S, S)
  for (const s of sorted) {
    const t = Math.min(1, Math.max(0, s.position / 100))
    grd.addColorStop(t, normalizeHex7(s.hex))
  }
  ctx.fillStyle = grd
  ctx.fillRect(0, 0, S, S)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  return tex
}
